import React, { useEffect, useState } from 'react';
import { Container } from '@infoods/lifebase-ui';

import { observer } from 'mobx-react';
import { useUnmount } from 'react-use';

import classes from './NgzEntityDashboard.module.scss';
import { angularizeWithProviders } from '../../../shared/providers';
import { DashboardBanner } from './DashboardBanner/DashboardBanner';
import { DashboardFilter } from './DashboardFilter/DashboardFilter';
import { OverviewWidget } from './OverviewWidget/OverviewWidget';
import { ScanningPeakHoursWidget } from './ScanningPeakHoursWidget/ScanningPeakHoursWidget';
import { GrossRevenueAnalyticsWidget } from './GrossRevenueAnalyticsWidget/GrossRevenueAnalyticsWidget';
import { ClientsAnalyticsWidget } from './ClientsAnalyticsWidget/ClientsAnalyticsWidget';
import { BestSellingWidget } from './BestSellingWidget/BestSellingWidget';
import { CoachPerformanceWidget } from './CoachPerformanceWidget/CoachPerformanceWidget';
import { entityDashboardFilterStore } from '../../stores/entityDashboardFilterStore';
import { entityDashboardService } from '../../services/entityDashboardService';
import { solutionsStore } from '../../../shared/stores/solutionsStore';
import { loggedInUserDataStore } from '../../../shared/stores/loggedInUserDataStore';
import { ScanAnalyticsWidget } from './ScanAnalyticsWidget/ScanAnalyticsWidget';
import { ClientsDemographicWidget } from './ClientsDemographicWidget/ClientsDemographicWidget';
import { RequiredAttentionWidget } from '../../../home';

const NgzEntityDashboard = observer(() => {
  const { solutionTypeFilter, solutionTypeFilterFlags, setDefaultValues } =
    entityDashboardFilterStore;

  const { solutionTypeFlags: availableSolutionsFlags } = solutionsStore;
  const { billingType, scanIqBillingType } = loggedInUserDataStore;

  // Pre-create the analytics row before the widgets fire their requests so the
  // scan-analytics/client-analytics calls don't race to create it (LMP-4850).
  const [isAnalyticsReady, setIsAnalyticsReady] = useState(false);

  const initCustomerAnalytics = async () => {
    await entityDashboardService.initCustomerAnalytics();
    setIsAnalyticsReady(true);
  };

  useEffect(() => {
    initCustomerAnalytics();
  }, []);

  useUnmount(setDefaultValues);

  const isShowNutritionWidgets =
    availableSolutionsFlags.NUTRITION &&
    (solutionTypeFilterFlags.ALL || solutionTypeFilterFlags.NUTRITION);

  // billingType defaults to AUTOMATED_BILLING even when the solution is not available,
  // so we also check available solutions to avoid false positives.
  const isNutritionAutomatedBilling =
    availableSolutionsFlags.NUTRITION && billingType.AUTOMATED_BILLING;
  const isScanIqAutomatedBilling =
    availableSolutionsFlags.SCAN_IQ && scanIqBillingType.AUTOMATED_BILLING;

  // Hide widget for Self-Billing solutions; in "All" view keep it visible
  // when at least one of the solutions uses Automated billing.
  const isShowGrossRevenueAnalyticsWidget =
    (solutionTypeFilterFlags.ALL &&
      (isNutritionAutomatedBilling || isScanIqAutomatedBilling)) ||
    (solutionTypeFilterFlags.NUTRITION && isNutritionAutomatedBilling) ||
    (solutionTypeFilterFlags.SCAN_IQ && isScanIqAutomatedBilling);

  if (!isAnalyticsReady) {
    return null;
  }

  return (
    <Container className={classes.container}>
      <DashboardBanner />
      <DashboardFilter />
      <div className={classes.widgetsContainer}>
        <div className={classes.row}>
          <OverviewWidget />
          <BestSellingWidget />
        </div>
        {isShowGrossRevenueAnalyticsWidget && <GrossRevenueAnalyticsWidget />}
        <div className={classes.row}>
          {isShowNutritionWidgets && <CoachPerformanceWidget />}
          <ScanAnalyticsWidget />
        </div>
        <ScanningPeakHoursWidget />
        <div className={classes.row}>
          <ClientsAnalyticsWidget />
          <ClientsDemographicWidget />
        </div>
        <RequiredAttentionWidget
          solutionType={solutionTypeFilter}
          unreadScansTableMode="ALL"
          paymentIssueTableMode="ALL"
          showPreRegisteredTable={isShowNutritionWidgets}
          titleTooltipContent={
            <>
              Unread scans, billing issues, leads
              <br />
              Tabs vary by features enabled
            </>
          }
        />
      </div>
    </Container>
  );
});

angularizeWithProviders(NgzEntityDashboard, 'ngzEntityDashboard', {});

export { NgzEntityDashboard };
